import * as yup from 'yup'
import { equals, isEmpty } from 'ramda'
import { HOTEL, ONE_WAY, MULTI_DESTINATION } from '../constants'
import { FlightRecordType } from '../types'
import { LOCATION_SEARCH_TYPE } from '../../../types'

const TRAVELERS_PER_ROOM = 4
const AIRPORT_ERROR = 'Please enter an airport'
const DISALLOW_INFANT_INTL_FLIGHT =
  'Sorry, Priceline does not offer lap infant seating on international flights'
const INFANT_CAPACITY_LIMIT = 'Only one infant per adult is allowed'
const ROOM_CAPACITY_ERROR =
  'Rooms accommodate up to 4 people. You may need additional rooms for your group'

const isInternational = (locationObject: LOCATION_SEARCH_TYPE) => {
  const countryCode = locationObject.countryCode.toUpperCase()
  return countryCode !== undefined && countryCode !== 'US'
}
const hasAnyInternational = (locationObjects: LOCATION_SEARCH_TYPE[]) =>
  locationObjects.some(isInternational)

function handleInfantTravel(this: yup.TestContext) {
  const { adultCount, flights, infantCount } = this.parent
  const hasInfant = infantCount > 0

  const isInValid = flights.some((flight: FlightRecordType) => {
    const { startLocation, endLocation } = flight
    const hasInternational = hasAnyInternational(
      [startLocation, endLocation].filter(Boolean)
    )
    return hasInfant && hasInternational
  })

  if (isInValid === true) {
    return this.createError({
      message: DISALLOW_INFANT_INTL_FLIGHT,
      path: this.path
    })
  }

  if (adultCount < infantCount) {
    return this.createError({
      message: INFANT_CAPACITY_LIMIT,
      path: this.path
    })
  }

  return true
}

function handleRoomCapacity(this: yup.TestContext) {
  const {
    adultCount,
    childrenCount,
    tripType,
    infantCount,
    roomCount,
    youthCount
  } = this.parent
  const travelerCount = adultCount + childrenCount + infantCount + youthCount
  const allowedTravelerCount = roomCount * TRAVELERS_PER_ROOM

  if (allowedTravelerCount < travelerCount && tripType.includes(HOTEL)) {
    return this.createError({
      message: ROOM_CAPACITY_ERROR,
      path: this.path
    })
  }
  return true
}

const SCHEMA_OW = yup.object().shape({
  endLocation: yup
    .object()
    .transform((val: LOCATION_SEARCH_TYPE) => {
      return isEmpty(val) || val === null ? undefined : val
    })
    .required(AIRPORT_ERROR)
    .test(
      'identical locations',
      "Departure and arrival airport can't be the same",
      function testOneWaySchema(this, val) {
        const ref = this.resolve(yup.ref('startLocation'))
        if (equals(ref, val)) {
          return false
        }
        return true
      }
    ),
  startDate: yup.string().required(),
  startLocation: yup
    .object()
    .transform((val: LOCATION_SEARCH_TYPE) => {
      return isEmpty(val) || val === null ? undefined : val
    })
    .required(AIRPORT_ERROR)
})

const SCHEMA_RT = SCHEMA_OW.concat(
  yup.object().shape({
    endDate: yup.string().required()
  })
)

const searchFormSchema = yup.object({
  flightType: yup.string(),
  flights: yup.array().when('flightType', {
    is: (val: string) => val === ONE_WAY || val === MULTI_DESTINATION,
    then: yup.array().of(SCHEMA_OW),
    otherwise: yup.array().of(SCHEMA_RT)
  }),
  infantCount: yup.number().test({
    name: 'infantCount',
    test: handleInfantTravel
  }),
  roomCount: yup
    .number()
    .test('roomCount', ROOM_CAPACITY_ERROR, handleRoomCapacity)
})

export default searchFormSchema
